import { useState, useEffect, useRef } from "react";
import { useModuleData } from "@/hooks/useModuleData";
import { useSite } from "@/context/SiteContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import { num, shortDate } from "@/lib/format";
import api from "@/lib/api";
import { Warehouse, Container, ArrowDownToLine, ArrowUpFromLine, Home, OctagonX, RotateCcw, Cog, Plus, CheckCircle2, Send, PackageCheck, ClipboardList, ScanLine, Radio, Play, Pause, Trash2, ArrowLeftRight, Tag } from "lucide-react";
import { toast } from "sonner";
import { openPdf } from "@/lib/pdf";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const CRANE_ICON = { IDLE: Cog, MOVING: Container, STORING: ArrowDownToLine, RETRIEVING: ArrowUpFromLine };

const KIND_STYLE = {
  HTBT: { color: "#64748b", label: "HEARTBEAT" },
  DPAI: { color: "#0ea5e9", label: "GOODS-IN" },
  DPWR: { color: "#0284c7", label: "DESPATCH" },
  UPPA: { color: "#10b981", label: "PUTAWAY" },
  UPPC: { color: "#22c55e", label: "SCAN" },
  UPMA: { color: "#f59e0b", label: "CRANE MOVE" },
  UPWC: { color: "#a855f7", label: "DESPATCH CONF" },
  USAC: { color: "#ec4899", label: "STOCK COUNT" },
  FNOREAD: { color: "#ef4444", label: "NO-READ" },
  APPERR: { color: "#ef4444", label: "APP ERROR" },
};

export const McsLinkMonitor = () => {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(true);
  const [feed, setFeed] = useState([]);
  const queue = useRef([]);
  const scroller = useRef(null);
  const runningRef = useRef(running);
  runningRef.current = running;
  const [fault, setFault] = useState(false);
  const faultRef = useRef(fault);
  faultRef.current = fault;

  useEffect(() => {
    if (!open) return;
    let alive = true;
    const tick = async () => {
      if (!alive || !runningRef.current) return;
      if (queue.current.length === 0) {
        try {
          const { data } = await api.get(`/asrs/mcs-telegrams?count=16&fault=${faultRef.current}`);
          queue.current = data.telegrams || [];
        } catch { /* keep trying */ }
      }
      const next = queue.current.shift();
      if (next) setFeed((f) => [...f.slice(-160), next]);
    };
    const iv = setInterval(tick, 650);
    return () => { alive = false; clearInterval(iv); };
  }, [open]);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [feed]);

  const sent = feed.filter((t) => t.dir === "SAP→MCS").length;
  const recv = feed.filter((t) => t.dir === "MCS→SAP").length;
  const acked = feed.filter((t) => t.ack).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="mcs-monitor-btn"
          className="ml-auto inline-flex items-center gap-2 rounded-lg border border-[#0284C7]/40 bg-[#0284C7]/10 px-3 py-1.5 text-xs font-semibold text-[#0284C7] hover:bg-[#0284C7]/20">
          <Radio size={14} /> MCS Link Monitor
        </button>
      </DialogTrigger>
      <DialogContent data-testid="mcs-monitor-dialog" className="max-w-3xl border-[#1f2733] bg-[#0b1016] text-[#cbd5e1]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#e2e8f0]">
            <ArrowLeftRight size={16} className="text-[#0ea5e9]" />
            SAP ↔ MCS Telegram Monitor
            <span className="ml-1 inline-flex items-center gap-1 rounded bg-emerald-500/15 px-2 py-0.5 font-mono text-[10px] text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> LINK ONLINE
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="mb-2 flex items-center gap-2 text-[11px]">
          <span className="rounded bg-[#0284C7]/15 px-2 py-1 font-mono text-[#38bdf8]">SAP→MCS {sent}</span>
          <span className="rounded bg-[#10b981]/15 px-2 py-1 font-mono text-[#34d399]">MCS→SAP {recv}</span>
          <span className="rounded bg-[#334155]/40 px-2 py-1 font-mono text-[#94a3b8]">ACK {acked}</span>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="mcs-fault-btn" onClick={() => setFault((f) => !f)}
              className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 font-mono text-[11px] ${fault ? "border-red-500 bg-red-500/15 text-red-400" : "border-[#334155] text-[#cbd5e1] hover:border-red-400"}`}>
              <OctagonX size={12} /> {fault ? "Faults ON" : "Inject Faults"}
            </button>
            <button data-testid="mcs-toggle-btn" onClick={() => setRunning((r) => !r)}
              className="inline-flex items-center gap-1 rounded-md border border-[#334155] px-2 py-1 font-mono text-[11px] text-[#cbd5e1] hover:border-[#0ea5e9]">
              {running ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Resume</>}
            </button>
            <button data-testid="mcs-clear-btn" onClick={() => { setFeed([]); queue.current = []; }}
              className="inline-flex items-center gap-1 rounded-md border border-[#334155] px-2 py-1 font-mono text-[11px] text-[#cbd5e1] hover:border-red-400">
              <Trash2 size={12} /> Clear
            </button>
          </div>
        </div>

        <div ref={scroller} data-testid="mcs-feed"
          className="h-[380px] overflow-y-auto rounded-lg border border-[#1f2733] bg-[#05080c] p-3 font-mono text-[11px] leading-relaxed">
          {feed.length === 0 && <div className="text-[#475569]">Waiting for telegrams on queue EUP / MCS…</div>}
          {feed.map((t, i) => {
            const st = KIND_STYLE[t.kind] || { color: "#94a3b8", label: t.kind };
            const toSap = t.dir === "MCS→SAP";
            const err = t.level === "error";
            return (
              <div key={`${t.seq}-${i}`} data-testid={`mcs-line-${t.seq}`} className={`mb-1.5 ${err ? "rounded border-l-2 border-red-500/70 bg-red-500/5 pl-1" : ""}`}>
                <div className="flex items-start gap-2">
                  <span className="text-[#475569]">{t.time}</span>
                  <span className={err ? "text-red-400" : toSap ? "text-emerald-400" : "text-sky-400"}>{err ? "✕ FAULT" : toSap ? "▲ MCS→SAP" : "▼ SAP→MCS"}</span>
                  <span className="rounded px-1.5 text-[9px]" style={{ backgroundColor: `${st.color}22`, color: st.color }}>{st.label}</span>
                  <span className="text-[#64748b]">[{t.queue}]</span>
                </div>
                <div className={`pl-[52px] break-all ${err ? "text-red-300" : "text-[#cbd5e1]"}`}>
                  &rarr; "{t.raw}" <span className="text-[#475569]">(len {t.length})</span>
                </div>
                <div className="pl-[52px] text-[#334155]">{t.desc}</div>
                {t.ack && <div className="pl-[52px] text-[#f59e0b]/70">&larr; ACK "{t.ack}"</div>}
                {err && <div className="pl-[52px] text-red-400">&#8635; NAK — telegram re-queued · operator alerted</div>}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CraneDetailDialog = ({ crane, onClose }) => {
  const [feed, setFeed] = useState([]);
  const [job, setJob] = useState(null);
  const scroller = useRef(null);
  useEffect(() => {
    if (!crane) { setFeed([]); setJob(null); return; }
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/asrs/cranes/${crane.crane_id}/telegrams?count=5`);
        if (!alive) return;
        setJob(data.current_job);
        setFeed((f) => [...f.slice(-100), ...data.telegrams]);
      } catch { /* keep polling */ }
    };
    load();
    const iv = setInterval(load, 2600);
    return () => { alive = false; clearInterval(iv); };
  }, [crane]);
  useEffect(() => { if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight; }, [feed]);

  return (
    <Dialog open={!!crane} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="crane-detail-dialog" className="max-w-2xl border-[#1f2733] bg-[#0b1016] text-[#cbd5e1]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#e2e8f0]">
            <Container size={16} className="text-[#EC4899]" /> {crane?.crane_id} · Live Link
            <span className="ml-1 rounded bg-[#334155]/50 px-2 py-0.5 font-mono text-[10px] text-[#cbd5e1]">Zone {crane?.zone}</span>
            <span className={`ml-auto inline-flex items-center gap-1 rounded px-2 py-0.5 font-mono text-[10px] ${crane?.status === "IDLE" ? "bg-slate-500/15 text-slate-300" : "bg-amber-500/15 text-amber-400"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${crane?.status === "IDLE" ? "bg-slate-400" : "bg-amber-400 animate-pulse"}`} /> {crane?.status}
            </span>
          </DialogTitle>
        </DialogHeader>

        {job && (
          <div data-testid="crane-current-job" className="rounded-lg border border-[#1f2733] bg-[#05080c] p-3 text-[12px]">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-semibold text-[#e2e8f0]">Current pallet job</span>
              <span className={`font-mono text-[10px] ${job.active ? "text-amber-400" : "text-[#475569]"}`}>{job.active ? job.op : "IDLE — no active job"}</span>
            </div>
            {job.active ? (
              <>
                <div className="grid grid-cols-2 gap-1.5 font-mono text-[11px] text-[#94a3b8]">
                  <div>Pallet <span className="text-[#cbd5e1]">{job.pallet}</span></div>
                  <div>Qty <span className="text-[#cbd5e1]">{num(job.qty)}</span></div>
                  <div className="col-span-2">Material <span className="text-[#cbd5e1]">{job.material_name}</span></div>
                  <div className="col-span-2">Route <span className="text-[#cbd5e1]">{job.from_loc} &rarr; {job.to_loc}</span></div>
                </div>
                <div className="mt-2 h-1.5 rounded bg-[#1f2733]"><div className="h-1.5 rounded bg-[#EC4899]" style={{ width: `${job.progress}%` }} /></div>
                <div className="mt-1 text-right font-mono text-[10px] text-[#64748b]">{job.progress}% complete</div>
              </>
            ) : <div className="font-mono text-[11px] text-[#64748b]">Crane idle at {crane?.position}. Awaiting task from MCS.</div>}
          </div>
        )}

        <div ref={scroller} data-testid="crane-detail-feed" className="mt-2 h-[260px] overflow-y-auto rounded-lg border border-[#1f2733] bg-[#05080c] p-3 font-mono text-[11px] leading-relaxed">
          {feed.length === 0 && <div className="text-[#475569]">Loading telegrams for {crane?.crane_id}…</div>}
          {feed.map((t, i) => {
            const st = KIND_STYLE[t.kind] || { color: "#94a3b8", label: t.kind };
            return (
              <div key={`${t.seq}-${i}`} className="mb-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-[#475569]">{t.time}</span>
                  <span className="text-emerald-400">▲ MCS→SAP</span>
                  <span className="rounded px-1.5 text-[9px]" style={{ backgroundColor: `${st.color}22`, color: st.color }}>{st.label}</span>
                  <span className="text-[#64748b]">[{t.queue}]</span>
                </div>
                <div className="pl-[52px] break-all text-[#cbd5e1]">&rarr; "{t.raw}"</div>
                <div className="pl-[52px] text-[#334155]">{t.desc}</div>
                {t.ack && <div className="pl-[52px] text-[#f59e0b]/70">&larr; ACK "{t.ack}"</div>}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function WarehouseAsrs() {
  const { data: cranes, loading, reload } = useModuleData("/asrs/cranes");
  const { data: bins } = useModuleData("/bins");
  const { data: moves } = useModuleData("/stock-movements");
  const { data: tos, reload: reloadTos } = useModuleData("/transfer-orders");
  const { data: wtasks, reload: reloadWt } = useModuleData("/warehouse-tasks");
  const { data: pinv, reload: reloadPi } = useModuleData("/physical-inventory");
  const [countVals, setCountVals] = useState({});
  const { sites, activeSite } = useSite();
  const [toOpen, setToOpen] = useState(false);
  const [toForm, setToForm] = useState({ type: "BIN_TO_BIN", qty: "", from_bin: "", to_bin: "", to_site: "", site_id: "" });
  const [detailCrane, setDetailCrane] = useState(null);

  if (loading || !cranes) return <Loader />;

  const command = async (crane, cmd) => {
    await api.post(`/asrs/cranes/${crane.crane_id}/command`, { command: cmd, target_bin: crane.position });
    const label = { STORE: "Store", RETRIEVE: "Retrieve", HOME: "Home", EMERGENCY_STOP: "E-STOP", RESET: "Reset" }[cmd];
    toast.success(`${label} command sent to ${crane.crane_id}`);
    reload();
  };

  const occAvg = bins?.length ? Math.round(bins.reduce((s, b) => s + b.occupancy_pct, 0) / bins.length) : 0;
  const activeCranes = cranes.filter((c) => c.status !== "IDLE").length;
  const totalCycles = cranes.reduce((s, c) => s + c.cycles_today, 0);

  const confirmTo = async (t) => {
    await api.post(`/transfer-orders/${t.id}/confirm`);
    toast.success(`Transfer ${t.to_no} confirmed & posted`);
    reloadTos();
  };
  const shipTo = async (t) => {
    await api.post(`/transfer-orders/${t.id}/ship`);
    toast.success(`${t.to_no} shipped → in transit to ${t.to_site}`);
    reloadTos();
  };
  const receiveTo = async (t) => {
    await api.post(`/transfer-orders/${t.id}/receive`);
    toast.success(`${t.to_no} received at ${t.to_site}`);
    reloadTos();
  };
  const createTo = async () => {
    const b = (bins || []).find((x) => x.bin_code === toForm.from_bin);
    try {
      if (toForm.type === "PLANT_TO_PLANT") {
        await api.post("/transfer-orders", {
          site_id: b?.site_id || "UK", material_code: b?.material_code, material_name: b?.material_name,
          qty: Number(toForm.qty), type: "PLANT_TO_PLANT", from_site: b?.site_id || "UK",
          to_site: toForm.to_site, from_bin: toForm.from_bin, to_bin: "",
        });
      } else {
        await api.post("/transfer-orders", {
          site_id: b?.site_id || (activeSite !== "GROUP" ? activeSite : sites[0]?.id),
          material_code: b?.material_code, material_name: b?.material_name,
          qty: Number(toForm.qty), from_bin: toForm.from_bin, to_bin: toForm.to_bin, type: "BIN_TO_BIN",
        });
      }
      toast.success("Transfer order created");
      setToOpen(false); setToForm({ type: "BIN_TO_BIN", qty: "", from_bin: "", to_bin: "", to_site: "", site_id: "" });
      reloadTos();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create transfer"); }
  };
  const confirmWt = async (w) => {
    try { await api.post(`/warehouse-tasks/${w.id}/confirm`); toast.success(`Warehouse task ${w.wt_no} confirmed`); reloadWt(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Confirm failed"); }
  };
  const saveCount = async (p) => {
    const v = countVals[p.id];
    if (v === undefined || v === "") { toast.error("Enter a counted quantity"); return; }
    try { await api.post(`/physical-inventory/${p.id}/count`, { counted_qty: Number(v) }); toast.success(`Count saved for ${p.doc_no}`); reloadPi(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Count failed"); }
  };
  const postPi = async (p) => {
    try { const { data } = await api.post(`/physical-inventory/${p.id}/post`); toast.success(`${p.doc_no} posted · difference ${num(data.difference)}`); reloadPi(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
  };

  const toColumns = [
    { key: "to_no", label: "Transfer", render: (r) => <span className="font-mono text-xs text-[#EC4899]">{r.to_no}</span> },
    { key: "type", label: "Type", render: (r) => <span className="font-mono text-[10px] text-[#6a6d70]">{r.type === "PLANT_TO_PLANT" ? "Plant→Plant" : "Bin→Bin"}</span> },
    { key: "material_name", label: "Material", render: (r) => (
      <div><div className="text-[#32363a]">{r.material_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{num(r.qty)} {r.uom}</div></div>) },
    { key: "from", label: "From", render: (r) => <span className="font-mono text-xs text-[#6a6d70]">{r.type === "PLANT_TO_PLANT" ? r.from_site : r.from_bin}</span> },
    { key: "to", label: "To", render: (r) => <span className="font-mono text-xs text-[#454b52]">{r.type === "PLANT_TO_PLANT" ? r.to_site : r.to_bin}</span> },
    { key: "status", label: "Status", badge: true },
    { key: "actions", label: "", render: (r) => (
      r.type === "BIN_TO_BIN" && r.status === "OPEN" ? (
        <button data-testid={`confirm-to-${r.to_no}`} onClick={() => confirmTo(r)}
          className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-[#eaedf2] px-2 py-1 text-[11px] hover:border-[#EC4899]"><CheckCircle2 size={13} /> Confirm &amp; Post</button>
      ) : r.type === "PLANT_TO_PLANT" && r.status === "OPEN" ? (
        <button data-testid={`ship-to-${r.to_no}`} onClick={() => shipTo(r)}
          className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-[#eaedf2] px-2 py-1 text-[11px] hover:border-[#0284C7]"><Send size={13} /> Ship</button>
      ) : r.status === "IN_TRANSIT" ? (
        <button data-testid={`receive-to-${r.to_no}`} onClick={() => receiveTo(r)}
          className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-600 hover:border-emerald-500"><PackageCheck size={13} /> Receive</button>
      ) : <span className="font-mono text-[10px] text-emerald-600">{r.status === "COMPLETED" ? "completed" : "posted"}</span>) },
  ];

  const binColumns = [
    { key: "bin_code", label: "Bin", render: (r) => <span className="font-mono text-xs text-[#EC4899]">{r.bin_code}</span> },
    { key: "zone", label: "Zone", render: (r) => <span className="font-mono">{r.zone}</span> },
    { key: "material_code", label: "Material", render: (r) => <span className="font-mono text-xs text-[#6a6d70]">{r.material_code}</span> },
    { key: "qty", label: "Qty", render: (r) => <span className="font-mono">{num(r.qty)}</span> },
    { key: "occupancy_pct", label: "Occupancy", render: (r) => (
      <div className="w-28"><div className="text-[10px] text-[#6a6d70]">{r.occupancy_pct}%</div>
        <div className="mt-1 h-1.5 rounded bg-[#eef1f5]"><div className="h-1.5 rounded bg-[#EC4899]" style={{ width: `${r.occupancy_pct}%` }} /></div></div>) },
    { key: "status", label: "Status", badge: true },
    { key: "actions", label: "", render: (r) => (
      <div className="flex items-center justify-end gap-1">
        <button data-testid={`bin-label-${r.bin_code}`} onClick={() => openPdf(`/print/label/bin/${encodeURIComponent(r.bin_code)}.pdf`)} title="Print bin location label (4x6)"
          className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] px-2 py-1 text-[11px] hover:border-[#EC4899]"><Tag size={13} /> Bin</button>
        {r.material_code && (
          <button data-testid={`bin-mat-label-${r.bin_code}`} onClick={() => openPdf(`/print/label/material/${encodeURIComponent(r.material_code)}.pdf`)} title="Print material label (4x6)"
            className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] px-2 py-1 text-[11px] hover:border-[#F97316]"><Tag size={13} /> Material</button>)}
      </div>) },
  ];

  const moveColumns = [
    { key: "timestamp", label: "Time", render: (r) => shortDate(r.timestamp) },
    { key: "movement_type", label: "Mvt Type", render: (r) => <span className="font-mono text-xs text-[#EC4899]">{r.movement_type}</span> },
    { key: "material_name", label: "Material" },
    { key: "qty", label: "Qty", render: (r) => <span className="font-mono">{num(r.qty)}</span> },
    { key: "ref", label: "Reference", render: (r) => <span className="font-mono text-xs text-[#6a6d70]">{r.ref}</span> },
  ];

  const wtColumns = [
    { key: "wt_no", label: "Task", render: (r) => <span className="font-mono text-xs text-[#EC4899]">{r.wt_no}</span> },
    { key: "task_type", label: "Type", render: (r) => <span className="font-mono text-[10px] text-[#6a6d70]">{r.task_type}</span> },
    { key: "material_name", label: "Material", render: (r) => (<div><div>{r.material_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{num(r.qty)} {r.uom}</div></div>) },
    { key: "from_bin", label: "Source", render: (r) => <span className="font-mono text-xs text-[#6a6d70]">{r.from_bin}</span> },
    { key: "to_bin", label: "Dest", render: (r) => <span className="font-mono text-xs text-[#454b52]">{r.to_bin}</span> },
    { key: "status", label: "Status", badge: true },
    { key: "actions", label: "", render: (r) => (r.status === "OPEN" ? (
      <button data-testid={`confirm-wt-${r.wt_no}`} onClick={() => confirmWt(r)} className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-[#eaedf2] px-2 py-1 text-[11px] hover:border-[#EC4899]"><CheckCircle2 size={13} /> Confirm</button>
    ) : <span className="font-mono text-[10px] text-emerald-600">confirmed</span>) },
  ];

  const piColumns = [
    { key: "doc_no", label: "PI Doc", render: (r) => <span className="font-mono text-xs text-[#EC4899]">{r.doc_no}</span> },
    { key: "material_name", label: "Material", render: (r) => (<div><div>{r.material_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{r.material_code} · {r.bin_code}</div></div>) },
    { key: "book_qty", label: "Book", render: (r) => <span className="font-mono">{num(r.book_qty)}</span> },
    { key: "counted_qty", label: "Counted", render: (r) => (r.status === "OPEN"
      ? <input data-testid={`pi-count-input-${r.doc_no}`} type="number" value={countVals[r.id] ?? ""} onChange={(e) => setCountVals({ ...countVals, [r.id]: e.target.value })} className="w-24 rounded-md border border-[#c8cfda] bg-white px-2 py-1 text-sm" />
      : <span className="font-mono">{num(r.counted_qty)}</span>) },
    { key: "difference", label: "Diff", render: (r) => r.difference == null ? "—" : <span className={`font-mono ${r.difference < 0 ? "text-red-600" : r.difference > 0 ? "text-emerald-600" : ""}`}>{r.difference > 0 ? "+" : ""}{num(r.difference)}</span> },
    { key: "status", label: "Status", badge: true },
    { key: "actions", label: "", render: (r) => (r.status === "OPEN" ? (
      <button data-testid={`pi-count-btn-${r.doc_no}`} onClick={() => saveCount(r)} className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-[#eaedf2] px-2 py-1 text-[11px] hover:border-[#EC4899]"><ScanLine size={13} /> Save Count</button>
    ) : r.status === "COUNTED" ? (
      <button data-testid={`pi-post-btn-${r.doc_no}`} onClick={() => postPi(r)} className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-600 hover:border-emerald-500"><PackageCheck size={13} /> Post Diff</button>
    ) : <span className="font-mono text-[10px] text-emerald-600">posted</span>) },
  ];

  return (
    <div>
      <PageHeader title="Warehouse & ASRS" subtitle="Storage bin telemetry plus a live control link to the Automated Storage & Retrieval System pallet cranes." icon={Warehouse} accent="#EC4899" />

      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="ASRS Cranes" value={cranes.length} accent="#EC4899" />
        <KpiCard label="Active Now" value={activeCranes} sub="storing / retrieving" accent="#10B981" />
        <KpiCard label="Cycles Today" value={num(totalCycles)} accent="#EC4899" />
        <KpiCard label="Avg Bin Occupancy" value={`${occAvg}%`} accent="#F59E0B" />
      </div>

      {/* ASRS Crane Control Panel */}
      <div className="mb-6 rounded-xl border border-[#e5e9f0] bg-[#ffffff] p-5">
        <div className="mb-4 flex items-center gap-2">
          <Container size={18} className="text-[#EC4899]" />
          <h3 className="text-lg font-semibold text-[#32363a]">ASRS Pallet Crane Control Link</h3>
          <span className="ml-2 font-mono text-[10px] rounded bg-emerald-500/15 px-2 py-0.5 text-emerald-600">● PLC LINK ONLINE</span>
          <McsLinkMonitor />
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {cranes.map((c) => {
            const Icon = CRANE_ICON[c.status] || Cog;
            const busy = c.status !== "IDLE";
            return (
              <div key={c.crane_id} data-testid={`crane-card-${c.crane_id}`}
                className="rounded-xl border border-[#e5e9f0] bg-[#eef1f5] p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-[#EC4899]/15 text-[#EC4899] ${busy ? "crane-active" : ""}`}>
                      <Icon size={18} />
                    </div>
                    <div>
                      <div className="font-mono text-sm text-[#32363a]">{c.crane_id}</div>
                      <div className="font-mono text-[10px] text-[#6a6d70]">Zone {c.zone} · pos {c.position}</div>
                    </div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div><div className="font-mono text-sm text-[#32363a]">{c.cycles_today}</div><div className="text-[10px] text-[#6a6d70]">cycles</div></div>
                  <div><div className="font-mono text-sm text-[#32363a]">{c.throughput}/h</div><div className="text-[10px] text-[#6a6d70]">throughput</div></div>
                  <div><div className={`font-mono text-sm ${c.health === "OK" ? "text-emerald-600" : "text-amber-600"}`}>{c.health === "OK" ? "OK" : "SVC"}</div><div className="text-[10px] text-[#6a6d70]">health</div></div>
                </div>
                {c.error && <div className="mt-2 rounded bg-red-500/15 px-2 py-1 text-[11px] text-red-600">{c.error}</div>}

                <button data-testid={`crane-detail-${c.crane_id}`} onClick={() => setDetailCrane(c)}
                  className="mt-3 inline-flex w-full items-center justify-center gap-1 rounded-md border border-[#c8cfda] bg-white px-2 py-1.5 text-[11px] font-medium text-[#454b52] hover:border-[#0284C7] hover:text-[#0284C7]">
                  <Radio size={12} /> Live telegram log &amp; job
                </button>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button data-testid={`crane-store-${c.crane_id}`} onClick={() => command(c, "STORE")}
                    className="inline-flex items-center justify-center gap-1 rounded-md border border-[#c8cfda] bg-[#eaedf2] px-2 py-1.5 text-[11px] text-[#32363a] hover:border-amber-500"><ArrowDownToLine size={13} /> Store</button>
                  <button data-testid={`crane-retrieve-${c.crane_id}`} onClick={() => command(c, "RETRIEVE")}
                    className="inline-flex items-center justify-center gap-1 rounded-md border border-[#c8cfda] bg-[#eaedf2] px-2 py-1.5 text-[11px] text-[#32363a] hover:border-cyan-500"><ArrowUpFromLine size={13} /> Retrieve</button>
                  <button data-testid={`crane-home-${c.crane_id}`} onClick={() => command(c, "HOME")}
                    className="inline-flex items-center justify-center gap-1 rounded-md border border-[#c8cfda] bg-[#eaedf2] px-2 py-1.5 text-[11px] text-[#32363a] hover:border-blue-500"><Home size={13} /> Home</button>
                  {c.error ? (
                    <button data-testid={`crane-reset-${c.crane_id}`} onClick={() => command(c, "RESET")}
                      className="inline-flex items-center justify-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1.5 text-[11px] text-emerald-600 hover:border-emerald-500"><RotateCcw size={13} /> Reset</button>
                  ) : (
                    <button data-testid={`asrs-crane-override-btn-${c.crane_id}`} onClick={() => command(c, "EMERGENCY_STOP")}
                      className="inline-flex items-center justify-center gap-1 rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1.5 text-[11px] text-red-600 hover:border-red-500"><OctagonX size={13} /> E-STOP</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Tabs defaultValue="bins">
        <TabsList className="bg-[#ffffff] border border-[#e5e9f0]">
          <TabsTrigger data-testid="tab-bins" value="bins" className="data-[state=active]:bg-[#eaedf2] data-[state=active]:text-[#EC4899]">Storage Bins</TabsTrigger>
          <TabsTrigger data-testid="tab-transfers" value="transfers" className="data-[state=active]:bg-[#eaedf2] data-[state=active]:text-[#EC4899]">Transfer Orders</TabsTrigger>
          <TabsTrigger data-testid="tab-wt" value="wt" className="data-[state=active]:bg-[#eaedf2] data-[state=active]:text-[#EC4899]">Warehouse Tasks</TabsTrigger>
          <TabsTrigger data-testid="tab-pi" value="pi" className="data-[state=active]:bg-[#eaedf2] data-[state=active]:text-[#EC4899]">Physical Inventory</TabsTrigger>
          <TabsTrigger data-testid="tab-moves" value="moves" className="data-[state=active]:bg-[#eaedf2] data-[state=active]:text-[#EC4899]">Stock Movements</TabsTrigger>
        </TabsList>
        <TabsContent value="bins" className="mt-4"><DataTable testId="bins-table" columns={binColumns} rows={bins || []} /></TabsContent>
        <TabsContent value="transfers" className="mt-4">
          <div className="mb-3 flex justify-end">
            <Dialog open={toOpen} onOpenChange={setToOpen}>
              <DialogTrigger asChild>
                <button data-testid="create-to-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#EC4899] px-4 py-2 text-sm font-semibold text-white hover:bg-[#db2777]"><Plus size={16} /> New Transfer Order</button>
              </DialogTrigger>
              <DialogContent className="border-[#e5e9f0] bg-white text-[#32363a]">
                <DialogHeader><DialogTitle className="uppercase">Create Transfer Order</DialogTitle></DialogHeader>
                <div className="grid gap-3">
                  <select data-testid="to-type-select" value={toForm.type} onChange={(e) => setToForm({ ...toForm, type: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm">
                    <option value="BIN_TO_BIN">Bin → Bin (same site)</option>
                    <option value="PLANT_TO_PLANT">Plant → Plant (inter-site, in-transit)</option>
                  </select>
                  <select data-testid="to-frombin-select" value={toForm.from_bin} onChange={(e) => setToForm({ ...toForm, from_bin: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm">
                    <option value="">Source bin…</option>
                    {(bins || []).filter((b) => b.status === "OCCUPIED").map((b) => <option key={b.id} value={b.bin_code}>{b.bin_code} · {b.material_name}</option>)}
                  </select>
                  {toForm.type === "PLANT_TO_PLANT" ? (
                    <select data-testid="to-tosite-select" value={toForm.to_site} onChange={(e) => setToForm({ ...toForm, to_site: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm">
                      <option value="">Destination plant…</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  ) : (
                    <input data-testid="to-tobin-input" placeholder="Destination bin (e.g. PCK-02-B-03)" value={toForm.to_bin} onChange={(e) => setToForm({ ...toForm, to_bin: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm" />
                  )}
                  <input data-testid="to-qty-input" type="number" placeholder="Quantity" value={toForm.qty} onChange={(e) => setToForm({ ...toForm, qty: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm" />
                </div>
                <DialogFooter>
                  <button data-testid="to-create-submit" onClick={createTo} disabled={!toForm.from_bin || !toForm.qty || (toForm.type === "PLANT_TO_PLANT" ? !toForm.to_site : !toForm.to_bin)}
                    className="rounded-lg bg-[#EC4899] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Create Transfer</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <DataTable testId="to-table" columns={toColumns} rows={tos || []} empty="No transfer orders." />
        </TabsContent>
        <TabsContent value="moves" className="mt-4"><DataTable testId="moves-table" columns={moveColumns} rows={moves || []} empty="No goods movements yet — post a goods receipt in Materials." /></TabsContent>
        <TabsContent value="wt" className="mt-4"><DataTable testId="wt-table" columns={wtColumns} rows={wtasks || []} empty="No warehouse tasks." /></TabsContent>
        <TabsContent value="pi" className="mt-4"><DataTable testId="pi-table" columns={piColumns} rows={pinv || []} empty="No physical inventory documents." /></TabsContent>
      </Tabs>
      <CraneDetailDialog crane={detailCrane} onClose={() => setDetailCrane(null)} />
    </div>
  );
}
